import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../../store/store';
import {useDropdownActionMenu} from '../../../hooks/useDropdownActionMenu';
import usePermissions from '../../../hooks/usePermissions';
import {Program} from '../../../types/program';
import {SearchType} from '../../../types/search';
import {setActiveTab} from '../clientSlice';
import {setIsEditing} from '../../payer/payerSlice';
import BlueButton from '../../../components/BlueButton';
import PlusIcon from '../../../assets/images/icons/PlusIcon';
import StatusBadge from '../../../components/StatusBadge';
import {ClientVisitThunks} from './clientVisitThunks';
import {ClientVisit} from '../../../types/clientVisit';
import {useNavigate} from 'react-router-dom';

const ClientVisitTable:  React.FC<{openDrawer: () => void; clientId?: string}> = ({openDrawer, clientId}) => {
  const clientVisits = useSelector(
    (state: RootState) => state.clientVisit.clientVisits,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.clientVisit.pagy);
  const activeTab = useSelector((state: RootState) => state.client.activeTab);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const programs = useSelector((state: RootState) => state.program.programs);
  const loading = useSelector(
    (state: RootState) => state.payer.status === 'loading',
  );
  const navigate = useNavigate();

  const programOptions = programs.map((program: Program) => ({
    value: program.id,
    label: program.name,
  }));

  const [visibleColumns, setVisibleColumns] = React.useState({
    id: true,
    initialDate: true,
    status: true,
    program: true,
  });
  interface VisibleColumnsType {
    id: boolean;
    initialDate: boolean;
    status: boolean;
    program: boolean;
  }
  const columnOptions = [
    {value: 'id', label: 'ID', isVisible: visibleColumns.id},
    {value: 'initialDate', label: 'Initial Date', isVisible: visibleColumns.initialDate},
    {value: 'status', label: 'Status', isVisible: visibleColumns.status},
    {value: 'program', label: 'Program', isVisible: visibleColumns.program},
  ];

  const searchByOptions = [
    {
      value: 'id',
      label: 'ID',
    },
  ];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    // Add clientId filter if provided
    if (clientId) {
      search.where = {
        ...search.where,
        client_id: {
          operator: '==',
          value: clientId,
        },
      };
    }

    await dispatch(
      ClientVisitThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenDrawer = (id: string) => async () => {
    dispatch(setIsEditing(false));
    openDrawer();
    await dispatch(ClientVisitThunks.show(id));
  };

  const handleVisitClick = (visitId: string) => {
    navigate(`/visits/${visitId}`);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={['Visits', 'Documents', 'Notes']}
      pageable={true}
      recordsName={'Visits'}
      searchable={true}
      columns={true}
      loading={loading}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      blueButton={
        checkPermission('create', 'Visit') ? (
          <BlueButton
            onClick={handleOpenDrawer('')}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.id && <TableHeader label="ID" sortBy="id" />}
          {visibleColumns.initialDate && <TableHeader label="Initial Date" sortBy="initialDate" />}
          {visibleColumns.status && <TableHeader label="Status" sortBy="status" />}
          {visibleColumns.program && <TableHeader label="Program" sortBy="program" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {clientVisits.map((visit:ClientVisit) => (
          <TableRow key={visit.id}>
            {visibleColumns.id &&
                <TableCell>
                  <div className=" relative group">
                    <div className="w-full flex items-center gap-x-3">
                      <div className="flex-grow">
                        <span
                          className="text-sm cursor-pointer font-medium dark:text-neutral-200 underline text-cyan-600 hover:text-cyan-800"
                          onClick={() => handleVisitClick(visit.id)}>
                          {visit.id}
                        </span>
                      </div>
                    </div>
                  </div>
                </TableCell>
              }
            {visibleColumns.initialDate && <TableCell>{visit.visitDate}</TableCell>}
            {visibleColumns.status && (
              <TableCell>
                <StatusBadge status={visit.status?.toString() || ''} />
              </TableCell>
            )}
            {visibleColumns.program && <TableCell>{visit.program?.name}</TableCell>}
          </TableRow>
        ))}
      </tbody>
    </Table>
  );
};

export default ClientVisitTable;
